import * as React from 'react';

export interface PageHeaderConfig {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  /** When set, both mobile and desktop render a back chevron */
  backPath?: string;
  /** Mobile-only back path — overrides backPath on mobile */
  mobileBackPath?: string;
  /** Desktop-only back path — overrides backPath on desktop */
  desktopBackPath?: string;
  /** Custom back handler for both surfaces — takes priority over all path fields */
  onBack?: () => void;
  /** Mobile-only back handler — overrides onBack on mobile */
  mobileOnBack?: () => void;
  /** Desktop-only back handler — overrides onBack on desktop */
  desktopOnBack?: () => void;
  /**
   * Changing this value triggers the header to re-sync its action button.
   * Use to swap the action when the active tab/section changes.
   */
  actionKey?: string;
  /**
   * Changing this value triggers the header to re-sync the title.
   * Required when title is a ReactNode — prevents infinite re-render loops.
   */
  titleKey?: string;
}

interface PageHeaderContextValue {
  config: PageHeaderConfig;
  setConfig: React.Dispatch<React.SetStateAction<PageHeaderConfig>>;
}

const PageHeaderContext = React.createContext<PageHeaderContextValue>({
  config: {},
  setConfig: () => undefined,
});

export function PageHeaderProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = React.useState<PageHeaderConfig>({});
  return (
    <PageHeaderContext.Provider value={{ config, setConfig }}>
      {children}
    </PageHeaderContext.Provider>
  );
}

export function usePageHeaderValue(): PageHeaderConfig {
  return React.useContext(PageHeaderContext).config;
}

export function usePageHeader({
  title,
  subtitle,
  action,
  backPath,
  mobileBackPath,
  desktopBackPath,
  onBack,
  mobileOnBack,
  desktopOnBack,
  actionKey,
  titleKey,
}: PageHeaderConfig): void {
  const { setConfig } = React.useContext(PageHeaderContext);
  const actionRef = React.useRef(action);
  actionRef.current = action;
  const titleRef = React.useRef(title);
  titleRef.current = title;
  const onBackRef = React.useRef(onBack);
  onBackRef.current = onBack;
  const mobileOnBackRef = React.useRef(mobileOnBack);
  mobileOnBackRef.current = mobileOnBack;
  const desktopOnBackRef = React.useRef(desktopOnBack);
  desktopOnBackRef.current = desktopOnBack;

  const hasMobileOnBack = mobileOnBack != null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: titleKey/actionKey/hasMobileOnBack are intentional change triggers — they force the effect to re-run and pick up the latest ref values for title and action
  React.useEffect(() => {
    setConfig({
      title: titleRef.current,
      subtitle,
      backPath,
      mobileBackPath,
      desktopBackPath,
      action: actionRef.current,
      onBack: onBackRef.current,
      mobileOnBack: mobileOnBackRef.current,
      desktopOnBack: desktopOnBackRef.current,
    });
    return () => setConfig({});
  }, [
    subtitle,
    backPath,
    mobileBackPath,
    desktopBackPath,
    setConfig,
    hasMobileOnBack,
    titleKey,
    actionKey,
  ]);
}
